'use client';

import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { useTranslations } from '@ifixit/i18n/hooks';
import { Card, CardContent } from '@ifixit/ui/components/card';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { WORKBENCH_PATH } from './index';
import { type WorkbenchPromptSource, trackWorkbenchPrompt } from './workbench-prompt-analytics';
import { hidePrompt, isPromptHidden } from './workbench-prompt-hidden';

export {
   WORKBENCH_PROMPT_SOURCES,
   type WorkbenchPromptSource,
   trackWorkbenchPrompt,
} from './workbench-prompt-analytics';
export { NOT_MINE_HIDE_DAYS } from './workbench-prompt-hidden';

/** The fields of GET workbench/items the prompt reads. */
export interface WorkbenchPromptItem {
   workbench_itemid: number;
   wikiid: number | null;
   variantid: number | null;
   nickname: string | null;
   title: string;
   display_title: string;
   variant: string | null;
   image: string | null;
}

export interface WorkbenchPromptProps {
   wikiid: number;
   /** The model the page is about, when it has one. Owned items on another model get a switch offer. */
   variantid?: number | null;
   /** The model's name, for the switch offer's label. */
   variant?: string | null;
   /** The device page's title, as the prompt names it. */
   title: string;
   image?: string | null;
   source: WorkbenchPromptSource;
   className?: string;
}

type Done =
   | { kind: 'saved'; item: WorkbenchPromptItem }
   | { kind: 'picked'; item: WorkbenchPromptItem }
   | { kind: 'switched'; item: WorkbenchPromptItem };

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const ITEMS_QUERY_KEY = ['workbench', 'items'];

const subscribeToNothing = () => () => {};

export function WorkbenchPrompt({
   wikiid,
   variantid = null,
   variant = null,
   title,
   image = null,
   source,
   className,
}: WorkbenchPromptProps) {
   const t = useTranslations('Workbench.Prompt');
   const apiClient = useIFixitApiClient();
   const queryClient = useQueryClient();
   // The server renders nothing: whether "Not mine" was tapped lives in the browser.
   const hiddenInStorage = useSyncExternalStore(
      subscribeToNothing,
      () => isPromptHidden(source, wikiid),
      () => true
   );
   const [closed, setClosed] = useState(false);
   const [done, setDone] = useState<Done | null>(null);
   const viewTracked = useRef(false);

   const itemsQuery = useQuery({
      queryKey: ITEMS_QUERY_KEY,
      queryFn: () =>
         apiClient.get<{ items: WorkbenchPromptItem[] }>('workbench/items', {
            statName: 'workbench-prompt-items',
         }),
      enabled: !hiddenInStorage && !closed,
   });
   // O(n) over the owner's items, n is one person's Workbench.
   const owned = useMemo(
      () => itemsQuery.data?.items?.filter(item => item.wikiid === wikiid) ?? [],
      [itemsQuery.data, wikiid]
   );
   const ownedCount = owned.length;

   useEffect(() => {
      if (!itemsQuery.isSuccess || viewTracked.current) {
         return;
      }
      viewTracked.current = true;
      trackWorkbenchPrompt('View', { source, owned_count: ownedCount });
   }, [itemsQuery.isSuccess, source, ownedCount]);

   const saveMutation = useMutation({
      mutationFn: () =>
         apiClient.post<WorkbenchPromptItem>('workbench/items', 'workbench-prompt-save', {
            headers: JSON_HEADERS,
            body: JSON.stringify({ wikiid, variantid: variantid ?? undefined, source }),
         }),
      onSuccess: item => {
         trackWorkbenchPrompt('Save', { source, owned_count: ownedCount });
         setDone({ kind: 'saved', item });
         void queryClient.invalidateQueries({ queryKey: ['workbench'] });
      },
   });

   const switchMutation = useMutation({
      mutationFn: (item: WorkbenchPromptItem) =>
         apiClient.patch<WorkbenchPromptItem>(
            `workbench/items/${item.workbench_itemid}`,
            'workbench-prompt-switch',
            {
               headers: JSON_HEADERS,
               body: JSON.stringify({ wikiid, variantid }),
            }
         ),
      onSuccess: item => {
         trackWorkbenchPrompt('Switch', { source, owned_count: ownedCount });
         setDone({ kind: 'switched', item });
         void queryClient.invalidateQueries({ queryKey: ['workbench'] });
      },
   });

   if (hiddenInStorage || closed || !itemsQuery.isSuccess) {
      return null;
   }

   const pick = (item: WorkbenchPromptItem) => {
      trackWorkbenchPrompt('Pick', { source, owned_count: ownedCount });
      setDone({ kind: 'picked', item });
   };

   const notMine = () => {
      trackWorkbenchPrompt('Not Mine', { source });
      hidePrompt(source, wikiid);
      setClosed(true);
   };

   const dismiss = () => {
      trackWorkbenchPrompt('Dismiss', { source });
      setClosed(true);
   };

   const isPending = saveMutation.isPending || switchMutation.isPending;
   const hasError = saveMutation.isError || switchMutation.isError;

   return (
      <Card className={cn('group relative', className)} data-test-id="workbench-prompt">
         <button
            type="button"
            className="button-ghost button-icon absolute top-2 right-2 size-8 text-gray-500 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-75"
            aria-label={t('dismiss')}
            onClick={dismiss}
         >
            <FaIcon name="xmark" className="size-4 fill-current" />
         </button>
         <CardContent className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 pr-10 sm:pr-12">
            {done ? (
               <DoneState done={done} variant={variant} />
            ) : (
               <>
                  <DeviceImage image={image} title={title} />
                  <StackBesideImage>
                     <div className="flex min-w-0 flex-col gap-1 self-center sm:self-auto">
                        <p className="m-0 text-base font-semibold text-gray-900">
                           {t(`${source}.title`, { device: title })}
                        </p>
                        <p className="m-0 text-sm text-gray-700">
                           {ownedCount > 0
                              ? ownedCount === 1
                                 ? t('ownedOne')
                                 : t('ownedMany', { count: ownedCount })
                              : t(`${source}.description`)}
                        </p>
                     </div>
                     {ownedCount > 0 && (
                        <ul className="col-span-2 m-0 flex list-none flex-col gap-2 p-0">
                           {owned.map(item => (
                              <OwnedItemRow
                                 key={item.workbench_itemid}
                                 item={item}
                                 matchesModel={!variantid || item.variantid === variantid}
                                 variant={variant}
                                 disabled={isPending}
                                 onPick={() => pick(item)}
                                 onSwitch={() => switchMutation.mutate(item)}
                              />
                           ))}
                        </ul>
                     )}
                     {hasError && (
                        <p role="alert" className="col-span-2 m-0 text-sm text-red-600">
                           {t('error')}
                        </p>
                     )}
                     <div className="col-span-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                        {ownedCount > 0 ? (
                           <button
                              type="button"
                              className="button-link button-sm px-0"
                              disabled={isPending}
                              onClick={() => saveMutation.mutate()}
                           >
                              {t('addAnother', { device: title })}
                           </button>
                        ) : (
                           <button
                              type="button"
                              className="button-primary button-md"
                              disabled={isPending}
                              onClick={() => saveMutation.mutate()}
                           >
                              <FaIcon name="toolbox" className="size-4 fill-current" />
                              {t('save')}
                           </button>
                        )}
                        <button
                           type="button"
                           className="button-ghost button-sm"
                           disabled={isPending}
                           onClick={notMine}
                        >
                           {t('notMine')}
                        </button>
                     </div>
                  </StackBesideImage>
               </>
            )}
         </CardContent>
      </Card>
   );
}

/**
 * From sm up, the text and buttons stack in the column beside the image, so the
 * image doesn't set the first row's height. Below sm the wrapper drops out and the
 * buttons take the card's full width under the image.
 */
function StackBesideImage({ children }: { children: ReactNode }) {
   return <div className="contents sm:flex sm:min-w-0 sm:flex-col sm:gap-3">{children}</div>;
}

function DeviceImage({ image, title }: { image: string | null; title: string }) {
   return (
      <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
         {image ? (
            <img src={image} alt={title} className="size-full object-contain" />
         ) : (
            <FaIcon name="toolbox" className="size-8 fill-current text-gray-400" />
         )}
      </div>
   );
}

function itemName(item: WorkbenchPromptItem): string {
   return item.nickname || item.display_title || item.title;
}

function itemDetail(item: WorkbenchPromptItem): string | null {
   const parts = item.nickname ? [item.display_title || item.title, item.variant] : [item.variant];
   return parts.filter(Boolean).join(' · ') || null;
}

function OwnedItemRow({
   item,
   matchesModel,
   variant,
   disabled,
   onPick,
   onSwitch,
}: {
   item: WorkbenchPromptItem;
   matchesModel: boolean;
   variant: string | null;
   disabled: boolean;
   onPick: () => void;
   onSwitch: () => void;
}) {
   const t = useTranslations('Workbench.Prompt');
   const detail = itemDetail(item);

   return (
      <li className="flex items-center gap-3 rounded-lg border border-gray-200 p-2">
         {item.image && (
            <img src={item.image} alt="" className="size-10 shrink-0 rounded-md object-cover" />
         )}
         <div className="flex min-w-0 flex-1 flex-col">
            <p className="m-0 truncate text-sm font-medium text-gray-900">{itemName(item)}</p>
            {detail && <p className="m-0 truncate text-xs text-gray-600">{detail}</p>}
         </div>
         <button
            type="button"
            className="button-outline button-sm shrink-0"
            disabled={disabled}
            onClick={matchesModel ? onPick : onSwitch}
         >
            {matchesModel
               ? t('pick')
               : variant
                 ? t('switchTo', { model: variant })
                 : t('switchToThisModel')}
         </button>
      </li>
   );
}

function DoneState({ done, variant }: { done: Done; variant: string | null }) {
   const t = useTranslations('Workbench.Prompt');
   const titles = {
      saved: t('savedTitle'),
      picked: t('pickedTitle'),
      switched: variant ? t('switchedTitle', { model: variant }) : t('switchedToThisModelTitle'),
   };
   const name = itemName(done.item);
   const detail = itemDetail(done.item);

   return (
      <>
         <div className="flex size-16 shrink-0 items-center justify-center rounded-lg bg-green-100">
            <FaIcon name="check" className="size-8 fill-current text-green-700" />
         </div>
         <StackBesideImage>
            <div className="flex min-w-0 flex-col gap-1 self-center sm:self-auto">
               <p className="m-0 text-base font-semibold text-gray-900">{titles[done.kind]}</p>
               <p className="m-0 text-sm text-gray-700">{detail ? `${name} · ${detail}` : name}</p>
            </div>
            <div className="col-span-2">
               <a
                  href={`${WORKBENCH_PATH}/${done.item.workbench_itemid}`}
                  className="button-outline button-md no-underline hover:no-underline"
               >
                  {t('openWorkbench')}
               </a>
            </div>
         </StackBesideImage>
      </>
   );
}
